import React, { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { products, imgUrl, productType } from "../utils/environments/api";
import Loader from "../Components/Common/loader/Loaders.jsx";
import "../assets/css/productsinglepage.css"

const ProductSinglePage = () => {
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("description");

  const location = useLocation();
  const id = new URLSearchParams(location.search).get("id");
  const [product, setProduct] = useState({});
  const [type, setType] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        const productResponse = await products.get(
          `get_products_api.php?id=${id}`
        );
        const typeResponse = await productType.get("get_product_typ_api.php");

        if (productResponse.data?.data) {
          setProduct(productResponse.data.data);
        } else {
          console.error("Invalid product response format:", productResponse);
        }

        if (typeResponse.data?.data) {
          setType(typeResponse.data.data);
        } else {
          console.error("Invalid type response format:", typeResponse);
        }
      } catch (error) {
        console.error("Error during API call:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id, location]);

  // Parse features from comma separated string to array
  const featuresArray = product.features ? product.features.split(', ') : [];
  
  // Parse specifications from comma separated string
  const parseSpecifications = (specsString) => {
    if (!specsString) return [];
    return specsString.split(', ').map(spec => {
      const [label, value] = spec.split(': ');
      return { label: label?.trim(), value: value?.trim() };
    });
  };
  
  const specifications = parseSpecifications(product.specifications);
  
  // Parse support items from comma separated string
  const parseSupportItems = (supportString) => {
    if (!supportString) return [];
    return supportString.split(', ').map(item => {
      const parts = item.split(' - ');
      const title = parts[0]?.trim();
      const descAndContact = parts[1]?.split(' (') || [];
      const description = descAndContact[0]?.trim();
      const contact = descAndContact[1]?.replace(')', '')?.trim();
      
      return { title, description, contact };
    });
  };
  
  const supportItems = parseSupportItems(product.support_items);

  // Handle brochure download
  const handleBrochureDownload = () => {
    if (product.brochure_file) {
      const brochureUrl = `${imgUrl}${product.brochure_file}`;
      window.open(brochureUrl, '_blank');
    } else {
      alert('Brochure not available for this product');
    }
  };

  // Handle quote request
  const handleQuoteRequest = () => {
    // You can implement quote request logic here
    alert(`Requesting quote for ${product.title}`);
  };

  return (
    <>
      {loading && <Loader />}
      <div className="product-single-page">
        {/* Hero Section */}
        <section className="page-title product-hero">
          <div
            className="pattern-layer-one"
          ></div>
          <div className="container">
            <div className="hero-content">
              <h1 className="hero-title">PRODUCT DETAIL</h1>
              <ul className="page-breadcrumb">
                <li>
                  <Link to="/" className="breadcrumb-link">
                    <i className="fas fa-home"></i> Home
                  </Link>
                </li>
                <li className="breadcrumb-separator">/</li>
                <li>
                  <Link to="/products" className="breadcrumb-link">Products</Link>
                </li>
                <li className="breadcrumb-separator">/</li>
                <li className="current-page">Product Detail</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Product Detail Section */}
        <section className="project-detail-section">
          <div className="container">
            <div className="product-grid">
              {/* Product Images & Basic Info */}
              <div className="product-main">
                <div className="product-gallery">
                  <div className="main-image">
                    <img
                      src={`${imgUrl}${product.image}`}
                      alt={product.title || "Product Image"}
                      className="product-image"
                    />
                    <div className="image-badge">Featured</div>
                  </div>
                  {/* You can add thumbnail gallery here if you have multiple images */}
                </div>

                <div className="product-info">
                  <h1 className="product-title">{product.title}</h1>
                  <h2 className="product-heading">{product.heading}</h2>
                  
                  {/* Dynamic Rating */}
                  <div className="product-rating">
                    <div className="stars">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <i 
                          key={star} 
                          className={`fas fa-star ${star <= (product.rating || 0) ? 'filled' : ''}`}
                          style={{ 
                            color: star <= (product.rating || 0) ? '#ffc107' : '#ddd' 
                          }}
                        ></i>
                      ))}
                    </div>
                    <span className="rating-text">
                      ({product.rating || '0.0'}/5.0 - {product.total_reviews || '0'} Reviews)
                    </span>
                  </div>

                  {/* Dynamic Features */}
                  <div className="product-features">
                    {featuresArray.length > 0 ? (
                      featuresArray.map((feature, index) => (
                        <div key={index} className="feature-item">
                          <i className="fas fa-check-circle"></i>
                          <span>{feature}</span>
                        </div>
                      ))
                    ) : (
                      <div className="feature-item">
                        <i className="fas fa-check-circle"></i>
                        <span>No features listed</span>
                      </div>
                    )}
                  </div>

                  <div className="action-buttons">
                    {/* <button className="btn btn-primary" onClick={handleQuoteRequest}>
                      <i className="fas fa-shopping-cart"></i>
                      Request Quote
                    </button> */}
                    <button 
                      className="btn btn-secondary" 
                      onClick={handleBrochureDownload}
                      disabled={!product.brochure_file}
                    >
                      <i className="fas fa-file-pdf"></i>
                      {product.brochure_file ? 'Download Brochure' : 'No Brochure'}
                    </button>
                  </div>
                </div>
              </div>

              {/* Product Details Tabs */}
              <div className="product-details-tabs">
                <div className="tabs-header">
                  <button 
                    className={`tab-button ${activeTab === 'description' ? 'active' : ''}`}
                    onClick={() => setActiveTab('description')}
                  >
                    <i className="fas fa-file-alt"></i>
                    Description
                  </button>
                  <button 
                    className={`tab-button ${activeTab === 'specifications' ? 'active' : ''}`}
                    onClick={() => setActiveTab('specifications')}
                  >
                    <i className="fas fa-list-alt"></i>
                    Specifications
                  </button>
                  <button 
                    className={`tab-button ${activeTab === 'features' ? 'active' : ''}`}
                    onClick={() => setActiveTab('features')}
                  >
                    <i className="fas fa-star"></i>
                    Key Features
                  </button>
                  <button 
                    className={`tab-button ${activeTab === 'support' ? 'active' : ''}`}
                    onClick={() => setActiveTab('support')}
                  >
                    <i className="fas fa-headset"></i>
                    Support
                  </button>
                </div>

                <div className="tabs-content">
                  {activeTab === 'description' && (
                    <div className="tab-panel active">
                      <h3>{product.content_heading || "Product Overview"}</h3>
                      <div 
                        className="product-content"
                        dangerouslySetInnerHTML={{ __html: product.content }}
                      ></div>
                      
                      <h3>{product.subcontent_heading || "Technical Details"}</h3>
                      <div
                        className="product-sub-content"
                        dangerouslySetInnerHTML={{ __html: product.sub_content }}
                      ></div>
                    </div>
                  )}

                  {activeTab === 'specifications' && (
                    <div className="tab-panel">
                      {specifications.length > 0 ? (
                        <div className="specs-grid">
                          {specifications.map((spec, index) => (
                            <div key={index} className="spec-item">
                              <span className="spec-label">{spec.label}</span>
                              <span className="spec-value">{spec.value}</span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="no-data">
                          <p>No specifications available for this product.</p>
                        </div>
                      )}
                    </div>
                  )}

                  {activeTab === 'features' && (
                    <div className="tab-panel">
                      {featuresArray.length > 0 ? (
                        <div className="features-grid">
                          {featuresArray.map((feature, index) => (
                            <div key={index} className="feature-card">
                              <i className="fas fa-check-circle"></i>
                              <h4>{feature}</h4>
                              <p>Premium {feature.toLowerCase()} for optimal performance</p>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="no-data">
                          <p>No features listed for this product.</p>
                        </div>
                      )}
                    </div>
                  )}

                  {activeTab === 'support' && (
                    <div className="tab-panel">
                      <div className="support-info">
                        {/* Main Support Contact */}
                        <div className="support-item">
                          <i className="fas fa-phone"></i>
                          <div>
                            <h4>Primary Support</h4>
                            <p>24/7 technical support available</p>
                            <span className="support-contact">
                              {product.support_phone || '+91 9100625636'}
                            </span>
                          </div>
                        </div>
                        
                        <div className="support-item">
                          <i className="fas fa-envelope"></i>
                          <div>
                            <h4>Email Support</h4>
                            <p>Get help via email</p>
                            <span className="support-contact">
                              {product.support_email || 'contact@vincosys.com'}
                            </span>
                          </div>
                        </div>

                        {/* Dynamic Support Items */}
                        {supportItems.length > 0 && supportItems.map((item, index) => (
                          <div key={index} className="support-item">
                            <i className="fas fa-headset"></i>
                            <div>
                              <h4>{item.title}</h4>
                              <p>{item.description}</p>
                              {item.contact && (
                                <span className="support-contact">{item.contact}</span>
                              )}
                            </div>
                          </div>
                        ))}

                        <div className="support-item">
                          <i className="fas fa-download"></i>
                          <div>
                            <h4>Documentation</h4>
                            <p>Download user manuals and guides</p>
                            <button className="btn-download">Download Resources</button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

             
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default ProductSinglePage;